import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { saveAs } from 'file-saver';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import AuthenticateService from '../auth/AuthenticateService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faDownload, faFilePdf } from '@fortawesome/free-solid-svg-icons';

const StatementPage = () => {
  const { http, token, user } = AuthenticateService();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Validate dates
  const validateDates = () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both from and to dates');
      return false;
    }

    if (fromDate > toDate) {
      toast.error('From date cannot be later than to date');
      return false;
    }

    // Check if date range is within reasonable limits (e.g., not more than 1 year)
    const oneYearLater = new Date(fromDate);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

    if (toDate > oneYearLater) {
      toast.error('Date range cannot exceed 1 year');
      return false;
    }

    return true;
  };

  // Preview statement (generates preview on server)
  const handlePreview = async () => {
    if (!validateDates()) return;

    setIsLoading(true);
    try {
      const response = await http.post('/statement/preview', {
        from: formatDate(fromDate),
        to: formatDate(toDate),
      });

      if (response.data.status === true) {
        // Show preview data in a modal or alert
        const previewData = response.data.data;

        toast.info(
          <div>
            <h6>Statement Preview</h6>
            <p><strong>Period:</strong> {formatDate(fromDate)} to {formatDate(toDate)}</p>
            <p><strong>Transactions:</strong> {previewData.transaction_count || 0}</p>
            <p><strong>Total Pages:</strong> {previewData.page_count || 1}</p>
          </div>,
          { autoClose: 5000 }
        );
      } else {
        toast.error(response.data.message || 'Failed to preview statement');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.message || 'Failed to preview statement');
    } finally {
      setIsLoading(false);
    }
  };

  // Download statement as PDF
  const handleDownload = async () => {
    if (!validateDates()) return;

    setIsDownloading(true);
    try {
      // For PDF download, we need to handle binary response
      const response = await fetch('https://core.agritechbanking.com/api/v1/statement/download', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify({
          from: formatDate(fromDate),
          to: formatDate(toDate),
        }),
      });

      if (response.ok) {
        const blob = await response.blob();

        // Generate filename
        const fromStr = formatDate(fromDate);
        const toStr = formatDate(toDate);
        const filename = `statement_${fromStr}_to_${toStr}.pdf`;

        // Save file using file-saver
        saveAs(blob, filename);

        toast.success('Statement downloaded successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to download statement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download statement. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
  };

  // Get today's date and 30 days ago as default suggestion
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      today,
      thirtyDaysAgo
    };
  };

  const { today, thirtyDaysAgo } = getDefaultDates();

  return (
    <>
      <Sidebar />
      <Header user={user} />

      <div className="content">
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-6">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faFilePdf} className="text-primary me-2" size="lg" />
                    <div>
                      <h4 className="mb-0">Account Statement</h4>
                      <p className="text-muted small mb-0">
                        Download your transaction history as a PDF document
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Date Selection Section */}
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                        From Date
                      </label>
                      <div className="input-group">
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          maxDate={new Date()}
                          placeholderText="Select start date"
                          className="form-control"
                          dateFormat="yyyy-MM-dd"
                          isClearable
                          showYearDropdown
                          scrollableYearDropdown
                        />
                        <span className="input-group-text">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                        </span>
                      </div>
                      {fromDate && (
                        <div className="mt-1">
                          <small className="text-muted">
                            Selected: {formatDate(fromDate)}
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                        To Date
                      </label>
                      <div className="input-group">
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate}
                          maxDate={new Date()}
                          placeholderText="Select end date"
                          className="form-control"
                          dateFormat="yyyy-MM-dd"
                          isClearable
                          showYearDropdown
                          scrollableYearDropdown
                        />
                        <span className="input-group-text">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                        </span>
                      </div>
                      {toDate && (
                        <div className="mt-1">
                          <small className="text-muted">
                            Selected: {formatDate(toDate)}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Date Presets */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Quick Select</label>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setFromDate(thirtyDaysAgo);
                          setToDate(today);
                        }}
                      >
                        Last 30 Days
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const threeMonthsAgo = new Date();
                          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                          setFromDate(threeMonthsAgo);
                          setToDate(today);
                        }}
                      >
                        Last 3 Months
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const sixMonthsAgo = new Date();
                          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                          setFromDate(sixMonthsAgo);
                          setToDate(today);
                        }}
                      >
                        Last 6 Months
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleReset}
                      >
                        Clear Dates
                      </button>
                    </div>
                  </div>

                  {/* Selected Range Display */}
                  {fromDate && toDate && (
                    <div className="alert alert-info mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Selected Period:</strong>
                          <div className="mt-1">
                            {formatDate(fromDate)} to {formatDate(toDate)}
                            <br />
                            <small className="text-muted">
                              {(Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)))} days
                            </small>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={handlePreview}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Previewing...' : 'Preview'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-grid gap-3">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleDownload}
                      disabled={isDownloading || !fromDate || !toDate}
                    >
                      {isDownloading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          Download Statement (PDF)
                        </>
                      )}
                    </button>

                    <div className="text-center">
                      <button
                        className="btn btn-link text-decoration-none"
                        onClick={handleReset}
                      >
                        Reset Selection
                      </button>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="mt-4 pt-3 border-top">
                    <h6 className="text-muted mb-2">
                      <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                      Important Information
                    </h6>
                    <ul className="small text-muted mb-0">
                      <li>Statements are generated in PDF format</li>
                      <li>Maximum date range: 1 year</li>
                      <li>You can download statements up to 5 years old</li>
                      <li>Make sure you have sufficient storage space for the PDF file</li>
                      <li>For large date ranges, download may take a few moments</li>
                    </ul>
                  </div>
                </div>

                <div className="card-footer bg-white border-top-0 py-3">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <FontAwesomeIcon icon={faFilePdf} className="text-primary" />
                        </div>
                        <div>
                          <small className="text-muted d-block">Format</small>
                          <small className="fw-semibold">PDF Document</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="bg-light rounded-circle p-2 me-3">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
                        </div>
                        <div>
                          <small className="text-muted d-block">Max Range</small>
                          <small className="fw-semibold">1 Year</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
};

export default StatementPage;